import React, { useState } from 'react'
import { Menu, Button } from '@amzn/alchemy-components-react'
import { useTranslation } from 'react-i18next'
import { RegisterMfaModalHooks } from "src/models/register-mfa-modal-hooks";
import { ManageScan2AuthModalHooks } from "src/models/manage-scan2auth-modal-hooks";
import { RemoveScopeModalHooks } from "src/models/remove-principal-scope-modal-hooks";
import { UpdateUserTypeModalHooks } from "src/models/update-user-type-modal-hooks";
import { MfaProvider, AuthgardMfaStatus, UserType } from "src/common/enums";

/**
 * Input parameters required to create Principal Action component.
 */
interface PrincipalActionProps {
  /**
   * ClassName to be used to render this Principal Action.
   */
  className: string

  /**
   * The unique identifier for the principal.
   * */
  principalArn: string

  /**
   * The full name of the principal.
   */
  fullName: string

  /**
   * The login/alias of the principal (unique handle shown alongside the name).
   * Optional — absent for principals without a resolved alias.
   */
  alias?: string

  /**
   * The tenant id of the scope.
   */
  tenantId: string

  /**
   * The scope of the principal.
   */
  scope: string

  /**
   * The user type of the principal.
   */
  userType: string

  /**
   * The MFA providers associated with this principal.
   * Can contain AUTHGARD_SCAN2AUTH, AUTHGARD_FIDO2, MILO, or combinations.
   */
  mfaProviders: string[]

  /**
   * The initial Authgard MFA status for this principal, resolved from bulkGetMfaStatus and used to
   * seed the manage modal. Undefined until the status fetch resolves (defaults to NO_RECORD).
   */
  initialMfaStatus?: AuthgardMfaStatus

  /**
   * The initial Authgard MFA invite expiry (raw ISO-8601 string, or null when there is no pending
   * invite). Passed through to the modal, which parses it and derives expired-ness from it.
   */
  initialMfaExpiry?: string | null

  /**
   * The hooks for the Register MFA modal.
   */
  registerMfaModalHooks: RegisterMfaModalHooks

  /**
   * The hooks for the Manage Scan2Auth / FIDO2 modal.
   */
  manageScan2AuthModalHooks: ManageScan2AuthModalHooks

  /**
   * The hooks for the Remove Scope modal.
   */
  removeScopeModalHooks: RemoveScopeModalHooks

  /**
   * The hooks for the Update User Type modal.
   */
  updateUserTypeModalHooks: UpdateUserTypeModalHooks
}

/**
 * Component to create Principal Action Dropdown.
 */
export const PrincipalAction = (principalActionProps: PrincipalActionProps) => {
  const { t } = useTranslation()

  const [menuIcon, setMenuIcon] = useState('caretDown')

  const hasScan2Auth = principalActionProps.mfaProviders?.includes(MfaProvider.AUTHGARD_SCAN2AUTH);
  // FIDO2 is admin-only — associates never see the "Manage FIDO2 Credentials" entry.
  const isAdmin = principalActionProps.userType !== UserType.ASSOCIATE;
  const hasFido2 = isAdmin && principalActionProps.mfaProviders?.includes(MfaProvider.AUTHGARD_FIDO2);

  const initializeRegisterMfaModal = () => {
    principalActionProps.registerMfaModalHooks.setPrincipalArn(principalActionProps.principalArn);
    principalActionProps.registerMfaModalHooks.setFullName(principalActionProps.fullName);
    principalActionProps.registerMfaModalHooks.setOpen(true);
  }

  const openAuthgardModal = (title: string, provider: 'SCAN2AUTH' | 'FIDO2') => {
    principalActionProps.manageScan2AuthModalHooks.setPrincipalArn(principalActionProps.principalArn);
    principalActionProps.manageScan2AuthModalHooks.setFullName(principalActionProps.fullName);
    principalActionProps.manageScan2AuthModalHooks.setAlias(principalActionProps.alias);
    principalActionProps.manageScan2AuthModalHooks.setModalTitle(title);
    principalActionProps.manageScan2AuthModalHooks.setInitialStatus(principalActionProps.initialMfaStatus || AuthgardMfaStatus.NO_RECORD);
    principalActionProps.manageScan2AuthModalHooks.setInitialExpiry(principalActionProps.initialMfaExpiry ?? null);
    principalActionProps.manageScan2AuthModalHooks.setMfaProvider(provider);
    principalActionProps.manageScan2AuthModalHooks.setScope(principalActionProps.scope);
    principalActionProps.manageScan2AuthModalHooks.setUserType(principalActionProps.userType);
    principalActionProps.manageScan2AuthModalHooks.setOpen(true);
  }

  const initializeRemoveScopeModal = () => {
    principalActionProps.removeScopeModalHooks.setPrincipalArn(principalActionProps.principalArn);
    principalActionProps.removeScopeModalHooks.setFullName(principalActionProps.fullName);
    principalActionProps.removeScopeModalHooks.setTenantId(principalActionProps.tenantId);
    principalActionProps.removeScopeModalHooks.setScope(principalActionProps.scope);
    principalActionProps.removeScopeModalHooks.setUserType(principalActionProps.userType);
    principalActionProps.removeScopeModalHooks.setOpen(true);
  }

  const initializeUpdateUserTypeModal = () => {
    principalActionProps.updateUserTypeModalHooks.setPrincipalArn(principalActionProps.principalArn);
    principalActionProps.updateUserTypeModalHooks.setFullName(principalActionProps.fullName);
    principalActionProps.updateUserTypeModalHooks.setTenantId(principalActionProps.tenantId);
    principalActionProps.updateUserTypeModalHooks.setScope(principalActionProps.scope);
    principalActionProps.updateUserTypeModalHooks.setUserType(principalActionProps.userType);
    principalActionProps.updateUserTypeModalHooks.setOpen(true);
  }

  // Render Remove Access in the error-alert color so admins can distinguish it
  // from routine menu items. Alchemy Menu's `customDisplay` accepts raw HTML
  // (rendered via lit-html's unsafe-html).
  const removeScopeLabel = t('remove-scope')
  const removePrincipalScope = {
    label: removeScopeLabel,
    customDisplay: `<span style="color: var(--color-base-error-alert, #c00);">${removeScopeLabel}</span>`,
    action: initializeRemoveScopeModal
  }

  const updateUserType = {
    label: t('update-user-type'),
    action: initializeUpdateUserTypeModal
  }

  // Build action menu options — Authgard options shown alongside each other when both present
  const actionMenuOptions: { label: string, action: () => void, customDisplay?: string }[] = []

  // The label doubles as the modal title, so resolve each once and reuse.
  const manageScan2AuthLabel = t('manage-scan2auth-credentials')
  const manageFido2Label = t('manage-fido2-credentials')

  if (hasScan2Auth) {
    actionMenuOptions.push({
      label: manageScan2AuthLabel,
      action: () => openAuthgardModal(manageScan2AuthLabel, 'SCAN2AUTH')
    });
  }

  if (hasFido2) {
    actionMenuOptions.push({
      label: manageFido2Label,
      action: () => openAuthgardModal(manageFido2Label, 'FIDO2')
    });
  }

  // If no Authgard providers, show existing MILO Register MFA
  if (!hasScan2Auth && !hasFido2) {
    actionMenuOptions.push({
      label: t('register-mfa'),
      action: initializeRegisterMfaModal
    });
  }

  actionMenuOptions.push(removePrincipalScope)
  actionMenuOptions.push(updateUserType)

  return (
    <Menu
      className={principalActionProps.className}
      id="principal-action-menu"
      options={actionMenuOptions}
      onClose={() => setMenuIcon('caretDown')}
      onOpen={() => setMenuIcon('caretUp')}
    >
      <span slot="trigger">
        <Button icon={menuIcon} iconPosition="right">
          {t('actions')}
        </Button>
      </span>
    </Menu>
  )
}
